import { EMPTY_VALUE } from '@/constants/ui'
import { DirtyFieldChange } from '@/hooks'

export const PASSWORD_MASK = '••••••••'

export interface FormChangeField {
  label: string
  format?: (value: unknown) => string
  image?: boolean
  items?: (value: unknown) => string[]
}

export type FormChangeFields<TValues extends object> = Partial<
  Record<keyof TValues & string, FormChangeField>
>

const normalize = (value: unknown) => (value === null || value === undefined ? '' : value)

const isSameValue = (prev: unknown, next: unknown): boolean => {
  const a = normalize(prev)
  const b = normalize(next)

  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item, index) => isSameValue(item, b[index]))
  }
  if (a instanceof File || b instanceof File) return a === b
  if (typeof a === 'object' && typeof b === 'object') return JSON.stringify(a) === JSON.stringify(b)

  return a === b
}

const formatScalar = (value: unknown, field: FormChangeField) => {
  const normalized = normalize(value)
  if (field.format) return field.format(normalized)
  return normalized === '' ? EMPTY_VALUE : String(normalized)
}

const toListDiff = (prev: string[], next: string[]) => {
  const prevSet = new Set(prev)
  const nextSet = new Set(next)

  return {
    nextItems: next.map((value) => ({ added: !prevSet.has(value), value })),
    prevItems: prev.map((value) => ({ removed: !nextSet.has(value), value })),
  }
}

export const getFormChanges = <TValues extends object>({
  defaultValues,
  fields,
  values,
}: {
  defaultValues: Readonly<object> | undefined
  fields: FormChangeFields<TValues>
  values: TValues
}): DirtyFieldChange[] =>
  (Object.entries(fields) as [keyof TValues & string, FormChangeField][]).flatMap(
    ([key, field]): DirtyFieldChange[] => {
      const prev = (defaultValues as Record<string, unknown> | undefined)?.[key]
      const next = values[key]
      if (isSameValue(prev, next)) return []

      if (field.items) {
        const prevItems = field.items(prev ?? [])
        const nextItems = field.items(next ?? [])
        const arrayDiff = toListDiff(prevItems, nextItems)
        const hasChanges =
          arrayDiff.prevItems.some((item) => item.removed) ||
          arrayDiff.nextItems.some((item) => item.added)

        return hasChanges ? [{ arrayDiff, label: field.label, nextValue: '', prevValue: '' }] : []
      }

      if (field.image) {
        const file = Array.isArray(next) ? next[0] : next
        if (!(file instanceof File)) return []

        return [
          {
            imageUrl: URL.createObjectURL(file),
            label: field.label,
            nextValue: file.name,
            prevValue: EMPTY_VALUE,
          },
        ]
      }

      return [
        {
          label: field.label,
          nextValue: formatScalar(next, field),
          prevValue: formatScalar(prev, field),
        },
      ]
    },
  )
